// Import necessary dependencies for React hooks, API, and components
import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api'; // Custom API module for making HTTP requests
import LocationAddModal from './modals/Location/locationAddModal'; // Modal component for adding/editing locations
import Pagination from '../Tools/Pagination'; // Component for pagination controls

// LocationTab component for managing EMS locations
const LocationTab = () => {
  // State management
  const [locations, setLocations] = useState([]); // Stores the list of locations
  const [filteredLocations, setFilteredLocations] = useState([]); // Stores filtered locations
  const [showModal, setShowModal] = useState(false); // Controls visibility of add/edit modal
  const [formData, setFormData] = useState({ 
    location_name: '', // Location name
    longitude: '', // Longitude coordinate
    latitude: '' // Latitude coordinate
  }); // Form data for location creation/editing
  const [isEditMode, setIsEditMode] = useState(false); // Tracks if modal is in edit mode
  const [isLoading, setIsLoading] = useState(false); // Loading state for API calls
  const [currentLocationId, setCurrentLocationId] = useState(null); // ID of location being edited
  const [page, setPage] = useState(1); // Current page for pagination
  const limit = 10; // Number of items per page
  const [searchTerm, setSearchTerm] = useState(''); // Search query for filtering locations

  /**
   * Displays an alert message to the user
   * @param {string} message - The message to display
   * @param {boolean} isSuccess - Whether it's a success message (default true)
   */
  const showAlert = (message, isSuccess = true) => {
    alert(`${isSuccess ? 'Success:' : 'Error:'} ${message}`);
  };

  /**
   * Fetches locations from the API
   * Handles success and error cases with appropriate user feedback
   */
  const fetchLocations = async () => {
    setIsLoading(true); // Set loading state
    try {
      const response = await axiosInstance.get('/ems/locations'); // Fetch locations
      const locationsData = response.data || []; // Default to empty array if no data
      setLocations(locationsData); // Update locations state
      setFilteredLocations(locationsData); // Initialize filtered locations
    } catch (error) {
      console.error('Error fetching locations:', error); // Log error
      showAlert('Failed to fetch locations', false); // Show error alert
      setLocations([]); // Reset locations
      setFilteredLocations([]); // Reset filtered locations
    } finally {
      setIsLoading(false); // Clear loading state
    }
  };

  // Fetch locations on component mount
  useEffect(() => {
    fetchLocations();
  }, []); // Empty dependency array ensures run on mount only

  // Filter locations based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredLocations(locations); // Show all locations if search is empty
      setPage(1); // Reset to first page
    } else {
      const filtered = locations.filter(location =>
        location.location_name.toLowerCase().includes(searchTerm.toLowerCase()) // Case-insensitive search
      );
      setFilteredLocations(filtered); // Update filtered locations
      setPage(1); // Reset to first page
    }
  }, [searchTerm, locations]); // Re-run when searchTerm or locations change

  /**
   * Clears the search filter
   */
  const clearFilters = () => {
    setSearchTerm(''); // Reset search term
    setPage(1); // Reset to first page
  };

  /**
   * Handles input changes for the form
   * @param {Object} e - The event object
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target; // Extract name and value
    setFormData((prev) => ({ ...prev, [name]: value })); // Update form data
  };

  /**
   * Opens the modal for adding a new location
   * Resets form data and sets edit mode to false
   */
  const openAddModal = () => {
    setIsEditMode(false); // Set to add mode
    setFormData({ 
      location_name: '',
      longitude: '',
      latitude: '' 
    }); // Reset form data
    setShowModal(true); // Show modal
  };

  /**
   * Prepares the modal for editing an existing location
   * @param {Object} location - The location data to edit
   */
  const handleEdit = (location) => {
    setIsEditMode(true); // Set to edit mode
    setCurrentLocationId(location.id); // Set location ID
    setFormData({ 
      location_name: location.location_name || '',
      longitude: location.longitude || '',
      latitude: location.latitude || '' 
    }); // Populate form with location data
    setShowModal(true); // Show modal
  };

  /**
   * Handles saving or updating a location
   * Shows appropriate success/error messages
   */
  const handleSave = async () => {
    try {
      setIsLoading(true); // Set loading state
      if (isEditMode) {
        await axiosInstance.put(`/ems/locations/${currentLocationId}`, formData); // Update location
        showAlert('Location updated successfully!'); // Show success alert
      } else {
        await axiosInstance.post('/ems/locations', formData); // Create location
        showAlert('Location added successfully!'); // Show success alert
      }
      setShowModal(false); // Close modal
      fetchLocations(); // Refresh location list
    } catch (error) {
      console.error('Save failed:', error); // Log error
      showAlert(error.response?.data?.message || 'Failed to save location', false); // Show error alert
    } finally {
      setIsLoading(false); // Clear loading state
    }
  };

  /**
   * Deletes a location after confirmation
   * @param {number} id - The ID of the location to delete
   */
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        setIsLoading(true); // Set loading state
        await axiosInstance.delete(`/ems/locations/${id}`); // Delete location
        showAlert('Location deleted successfully!'); // Show success alert
        fetchLocations(); // Refresh location list
      } catch (error) {
        console.error('Delete failed:', error); // Log error
        showAlert('Failed to delete location', false); // Show error alert
      } finally {
        setIsLoading(false); // Clear loading state
      }
    }
  };

  // Pagination calculations
  const currentItems = filteredLocations.slice((page - 1) * limit, page * limit); // Items for current page
  const total = filteredLocations.length; // Total number of filtered locations

  // Render the component
  return (
    <div className="tab-pane active" id="bs-location" role="tabpanel">
      {/* Filter and Add Button Section */}
      <div className="filter-search mt-3">
        <h6 className="px-2">Filter</h6>
        <div className="filter-panel">
          <form className="row">
            <div className="form-group col-md-4 col-lg-4 col-12 p-2">
              <input 
                type="text" 
                className="form-control" 
                placeholder="Search By Location" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} // Update search term
              />
            </div>
            <div className="form-group col-lg-3 p-2">
              <button 
                type="button" 
                className="btn btn-outline-primary btn-rounded"
                onClick={clearFilters}
              >
                Clear Filter
              </button>
            </div>
          </form>
          <button
            className="btn m-2 d-flex align-items-center justify-content-center btn-outline-primary"
            onClick={openAddModal}
          >
            <i className="ti ti-circle-plus fs-4 me-2"></i>
            Add Location
          </button>
        </div>
      </div>

      {/* Locations Table */}
      {isLoading ? (
        <div className="text-center py-4">Loading locations...</div>
      ) : (
        <div className="py-3 px-0">
          <div className="table-responsive rounded-2 ros-table">
            <table className="table text-nowrap customize-table mb-0 align-middle">
              <thead className="text-dark fs-4">
                <tr>
                  <th><h6 className="fw-semibold mb-0">ID</h6></th>
                  <th><h6 className="fw-semibold mb-0">Location Name</h6></th>
                  <th><h6 className="fw-semibold mb-0">Latitude</h6></th>
                  <th><h6 className="fw-semibold mb-0">Longitude</h6></th>
                  <th><h6 className="fw-semibold mb-0">Actions</h6></th>
                </tr>
              </thead>
              <tbody>
                {filteredLocations.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4">
                      {searchTerm ? 'No matching locations found' : 'No Locations Found'} {/* Conditional message */}
                    </td>
                  </tr>
                ) : (
                  currentItems.map((loc, index) => (
                    <tr key={loc.id}>
                      <td>{(page - 1) * limit + index + 1}</td> {/* Calculate display ID */}
                      <td>{loc.location_name}</td>
                      <td>{loc.latitude || 'N/A'}</td>
                      <td>{loc.longitude || 'N/A'}</td>
                      <td>
                        <button
                          className="h4 me-3 border-0 bg-transparent"
                          onClick={() => handleEdit(loc)}
                          aria-label="Edit location"
                        >
                          <i className="ti ti-edit text-warning"></i>
                        </button>
                        <button
                          className="h4 border-0 bg-transparent"
                          onClick={() => handleDelete(loc.id)}
                          aria-label="Delete location"
                        >
                          <i className="ti ti-trash text-danger"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Pagination - only shown when there are locations */}
      {filteredLocations.length > 0 && (
        <Pagination
          total={total}
          limit={limit}
          page={page}
          onPageChange={setPage}
        />
      )}
      
      {/* Add/Edit Location Modal */}
      <LocationAddModal
        show={showModal}
        onClose={() => setShowModal(false)} // Close modal
        formData={formData}
        onChange={handleInputChange}
        onSave={handleSave}
        isEditMode={isEditMode}
        isLoading={isLoading}
      />
    </div>
  );
};

// Export the component
export default LocationTab;